import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Award,
  Check,
  CheckCheck,
  ChevronLeft,
  ClipboardList,
  Download,
  Eye,
  FileText,
  Landmark,
  Loader2,
  Mail,
  Package,
  Repeat,
  ShoppingCart,
  Truck,
  Upload,
} from 'lucide-react';
import { useIsReadOnly } from '@/shared/auth/SessionProvider';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/lib/cn';
import { toast } from 'sonner';
import { authHeaders } from '@/shared/api/client';
import {
  bitacoraPhotoUrl,
  enviarCertificadoEmail,
  getMantenimientoDetail,
  listMantenimientoDetalles,
  type MantenimientoContacto,
  type MantenimientoDetailResponse,
  type MantenimientoRow,
} from './api';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { EmailConfirmDialog } from '@/shared/components/ui/email-confirm-dialog';
import { BitacorasQuickDialog } from './components/BitacorasQuickDialog';
import { CambiarHornoDialog } from './components/CambiarHornoDialog';
import { CertificadoGarantiaDialog } from './components/CertificadoGarantiaDialog';
import { ConfirmarDespachoDialog } from './components/ConfirmarDespachoDialog';
import { ConfirmarNroDespachoDialog } from './components/ConfirmarNroDespachoDialog';
import { NumerosSerieDespachoDialog } from './components/NumerosSerieDespachoDialog';
import { PuestaMarchaDialog } from './components/PuestaMarchaDialog';
import { VentaDetallesDialog } from './components/VentaDetallesDialog';
import { echeqSaldoDetalle, saldoEnDespacho, useSaldosEcheq } from '@/shared/components/EcheqSaldoTag';
import { DespachoDetallesDialog } from '@/features/ventas/components/DespachoDetallesDialog';
import { PuestaMarchaDetallesDialog } from '@/features/ventas/components/PuestaMarchaDetallesDialog';

/**
 * Post-payment fulfillment detail page. Replaces the old modal-based
 * `DetalleMantenimientoDialog`. Matches the PowerApps
 * Screen_DetalleMantenimiento layout: a customer strip at the top,
 * then Despacho + Puesta Marcha cards with their action pills.
 *
 * Each action still fires into the existing M2/M3/M4 dialogs —
 * this page is just a landing surface with correct state gating.
 */
export function DetalleMantenimientoPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const saldos = useSaldosEcheq();

  const detailQ = useQuery({
    queryKey: ['mantenimiento', 'detail', activeCountry.code, id] as const,
    queryFn: () => getMantenimientoDetail(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
  // Detalles for the "Confirmar N° Despacho" pre-flight: every line
  // needs both NroSerie and NroDespacho before the confirmation can
  // be granted. Reuses the same cache key the NumerosSerieDespacho
  // dialog uses, so the data is shared across views.
  const detallesQ = useQuery({
    queryKey: ['mantenimiento', 'detalles', activeCountry.code, id] as const,
    queryFn: () => listMantenimientoDetalles(id!),
    enabled: Boolean(id),
    staleTime: 30_000,
  });

  // Sub-dialog state — keep them independent so the user can
  // hop between the pills without losing context.
  const [dialog, setDialog] = useState<
    | 'bitacoras'
    | 'detalles'
    | 'cambiar-horno'
    | 'numeros'
    | 'confirmar-nro-despacho'
    | 'realizar-despacho'
    | 'despacho-detalles'
    | 'puesta-marcha'
    | 'puesta-marcha-detalles'
    | 'certificado'
    | 'certificado-email'
    | null
  >(null);
  // Pre-flight error dialog when "Confirmar N° Despacho" is clicked
  // but at least one line is missing serie/despacho. Declared up here
  // so it sits above the conditional `isPending`/`isError` early
  // returns — moving any hook below those branches breaks the
  // "same hooks every render" rule.
  const [missingDataDialogOpen, setMissingDataDialogOpen] = useState(false);

  if (detailQ.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }
  if (detailQ.isError || !detailQ.data) {
    return (
      <div className="space-y-4">
        <Button
          variant="outline"
          onClick={() => navigate('/mantenimiento')}
          className="gap-2"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
          Volver
        </Button>
        <div className="rounded-2xl border border-dashed bg-card/40 p-10 text-center">
          <p className="text-base font-medium text-foreground">Venta no encontrada</p>
          <p className="mt-1 text-sm text-muted-foreground">
            La venta #{id} no existe o no tiene seguimiento de mantenimiento.
          </p>
        </div>
      </div>
    );
  }

  const data = detailQ.data;
  const row = detailToRow(data);
  const { progress, contacto, fields } = data;

  const isReadOnly = useIsReadOnly();
  const saldoEcheq = saldoEnDespacho(saldos, data.id, fields.Status_RO);
  const statusAnulado = (fields.Status_RO ?? '') === 'Anulado';
  // The "Confirmar N° Despacho" pill only makes sense when the
  // physical despacho form hasn't been filed yet; after that, the
  // flag is usually a redundant badge.
  // Retenida por E-cheqs: nada del despacho hasta que se cobre el %.
  const canConfirmarNroDespacho =
    !statusAnulado && !progress.nroDespachoConfirmado && !saldoEcheq?.retenido && !isReadOnly;
  // Realizar Despacho is gated behind Confirmar N° Despacho — the
  // admin has to acknowledge the paperwork numbers before the
  // physical despacho can be filed. (The pill stays clickable when
  // `despachado` is already true so the read-only viewer is reachable.)
  // E-cheqs sin acreditar con lo cobrado por debajo del % del cliente:
  // retenida (el server también lo frena).
  const canRealizarDespacho =
    !statusAnulado &&
    !progress.despachado &&
    progress.nroDespachoConfirmado &&
    !saldoEcheq?.retenido &&
    !isReadOnly;

  // Nova-flow ventas (MX) can't be swapped — Factura Interna / Caja
  // Diferencia bookkeeping would go inconsistent, so we block it end to end.
  const esNova = (fields.FNova_RO ?? '') === 'SI';
  // Cambiar horno is only offered while the venta is in Pendiente Despacho
  // and not yet dispatched — the window where items can't be edited on the
  // venta but the client can still change their oven. The swap itself needs
  // admin approval.
  const canCambiarHorno =
    !statusAnulado &&
    !progress.despachado &&
    (fields.Status_RO ?? '') === 'Pendiente Despacho' &&
    !esNova &&
    !isReadOnly;
  // Currency for the swap dialog: USD ventas price against the dollar
  // list, everything else against the country's home currency.
  const monedaVenta: 'ARS' | 'MXN' | 'USD' =
    (fields.Moneda_RO ?? '') === 'USD'
      ? 'USD'
      : activeCountry.code === 'MX'
        ? 'MXN'
        : 'ARS';
  // Facturación % drives the owed-vs-sticker weighting in the swap preview.
  // Blank/legacy => 100 (AR is always 100; MX Nova ventas are blocked above).
  const porcentajeFacturaVenta = (() => {
    const raw = (fields.PorcentajeFactura_RO ?? '').toString().replace(',', '.');
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.min(100, n) : 100;
  })();

  // Articles missing either Nro Serie or Nro Despacho — the admin
  // can't confirm the dispatch numbers while any line is incomplete.
  // Non-serie items (cuenta contable says NroSerie=NO, e.g. Accesorios)
  // are exempt: those lines render "—" and never need user input, so
  // they shouldn't block confirmar despacho. Lazily evaluated; empty
  // while detalles are still loading.
  const articulosFaltantes = (detallesQ.data ?? []).filter(
    (d) => d.llevaNroSerie && (!d.nroSerie.trim() || !d.nroDespacho.trim()),
  );

  // Editing serial/dispatch numbers stops being allowed once the
  // physical despacho is filed — those numbers are now part of the
  // shipment record. (Anulado was already read-only.)
  const numerosReadOnly = statusAnulado || progress.despachado || isReadOnly;
  const canPuestaMarcha =
    !statusAnulado && progress.despachado && !progress.puestaMarchaCargada && !isReadOnly;
  const canCertificado =
    !statusAnulado &&
    progress.puestaMarchaCargada &&
    !progress.certificadoCargado &&
    !isReadOnly;

  const afterMutation = () => {
    qc.invalidateQueries({ queryKey: ['mantenimiento'] });
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto pb-4">
      <div className="flex shrink-0 items-center gap-3">
        <Button
          size="icon"
          className="h-10 w-10 shrink-0 rounded-full bg-foreground text-background hover:bg-foreground/90"
          onClick={() => navigate('/mantenimiento')}
          aria-label="Volver a Mantenimiento"
          title="Volver a Mantenimiento"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          Mantenimiento
        </h1>
      </div>

      <ContactoStrip contacto={contacto} ventaId={data.id} />

      {saldoEcheq && (
        <div
          className={cn(
            'flex shrink-0 items-start gap-2 rounded-lg border px-3 py-2 text-sm',
            saldoEcheq.retenido
              ? 'border-rose-300 bg-rose-50 text-rose-900'
              : 'border-orange-300 bg-orange-50 text-orange-900',
          )}
        >
          <Landmark className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div className="min-w-0">
            <div className="font-medium">
              {saldoEcheq.retenido ? 'Despacho retenido: faltan cobrar E-cheqs' : 'Pago parcial con E-cheqs'}
            </div>
            <div className="text-xs opacity-90">{echeqSaldoDetalle(saldoEcheq, true)}</div>
          </div>
        </div>
      )}

      <DespachoCard
        anulado={statusAnulado}
        progress={progress}
        canConfirmarNroDespacho={canConfirmarNroDespacho}
        canRealizarDespacho={canRealizarDespacho}
        canCambiarHorno={canCambiarHorno}
        cambiarHornoReason={
          esNova
            ? 'No disponible: la venta tiene flujo Nova (Factura Interna / Caja Diferencia).'
            : undefined
        }
        onCambiarHorno={() => setDialog('cambiar-horno')}
        onViewBitacoras={() => setDialog('bitacoras')}
        onViewDetalles={() => setDialog('detalles')}
        onOpenNumeros={() => setDialog('numeros')}
        onConfirmarNroDespacho={() => {
          // Pre-flight: if any line is missing serie or despacho,
          // route the admin to the error dialog instead of opening
          // the confirmation flow. Pass the lookup through the
          // detalles query so it stays fresh after edits.
          if (articulosFaltantes.length > 0) {
            setMissingDataDialogOpen(true);
            return;
          }
          setDialog('confirmar-nro-despacho');
        }}
        onRealizarDespacho={() =>
          setDialog(
            progress.despachado ? 'despacho-detalles' : 'realizar-despacho',
          )
        }
      />

      <PuestaMarchaCard
        canPuestaMarcha={canPuestaMarcha}
        canCertificado={canCertificado}
        progress={progress}
        certificadoDriveItemId={fields.CertificadoDriveItemID_RO ?? null}
        onPuestaMarcha={() =>
          setDialog(
            progress.puestaMarchaCargada
              ? 'puesta-marcha-detalles'
              : 'puesta-marcha',
          )
        }
        onCertificado={() => setDialog('certificado')}
        onEnviarCertificadoEmail={() => setDialog('certificado-email')}
      />

      {dialog === 'bitacoras' && (
        <BitacorasQuickDialog row={row} onClose={() => setDialog(null)} />
      )}
      {dialog === 'detalles' && (
        <VentaDetallesDialog row={row} onClose={() => setDialog(null)} />
      )}
      {dialog === 'cambiar-horno' && (
        <CambiarHornoDialog
          ventaId={data.id}
          moneda={monedaVenta}
          porcentajeFactura={porcentajeFacturaVenta}
          detalles={detallesQ.data ?? []}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'numeros' && (
        <NumerosSerieDespachoDialog
          ventaId={data.id}
          disabled={numerosReadOnly}
          disabledReason={
            statusAnulado
              ? 'La venta está anulada — los números no se pueden editar.'
              : progress.despachado
                ? 'El despacho ya fue realizado, ya no se pueden editar los números de serie ni de despacho.'
                : undefined
          }
          nroDespachoConfirmado={progress.nroDespachoConfirmado}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'confirmar-nro-despacho' && (
        <ConfirmarNroDespachoDialog
          ventaId={data.id}
          onClose={() => setDialog(null)}
          onConfirmed={() => {
            setDialog(null);
            afterMutation();
          }}
        />
      )}
      {dialog === 'realizar-despacho' && (
        <ConfirmarDespachoDialog
          summary={row}
          onClose={() => {
            setDialog(null);
            afterMutation();
          }}
        />
      )}
      <DespachoDetallesDialog
        open={dialog === 'despacho-detalles'}
        onClose={() => setDialog(null)}
        recibe={fields.RecibeDespacho_RO}
        direccion={fields.DireccionCompletaDespacho_RO}
        observaciones={fields.ObsDespacho_RO}
        fotoIdsJson={fields.DespachoFotoIds_RO}
        fotoTiposJson={fields.DespachoFotoTipos_RO}
      />
      <MissingNumerosDialog
        open={missingDataDialogOpen}
        items={articulosFaltantes}
        onClose={() => setMissingDataDialogOpen(false)}
        onOpenNumeros={() => {
          setMissingDataDialogOpen(false);
          setDialog('numeros');
        }}
      />
      {dialog === 'puesta-marcha' && (
        <PuestaMarchaDialog
          summary={row}
          onClose={() => {
            setDialog(null);
            afterMutation();
          }}
        />
      )}
      <PuestaMarchaDetallesDialog
        open={dialog === 'puesta-marcha-detalles'}
        onClose={() => setDialog(null)}
        ventaId={data.id}
        instalacionId={fields.VideoInstalacionDriveItemID_RO}
        llamaId={fields.VideoLlamaDriveItemID_RO}
        reguladorId={fields.VideoReguladorDriveItemID_RO}
        multimediaId={fields.VideoMultimediaDriveItemID_RO}
      />
      {dialog === 'certificado' && (
        <CertificadoGarantiaDialog
          summary={row}
          onClose={() => {
            setDialog(null);
            afterMutation();
          }}
        />
      )}
      <EmailConfirmDialog
        open={dialog === 'certificado-email'}
        title="Enviar certificado por correo"
        description={
          contacto.nombre.trim()
            ? `El correo se enviará a ${contacto.nombre.trim()} con el Certificado de Garantía adjunto en PDF.`
            : 'El correo incluirá el Certificado de Garantía adjunto en PDF.'
        }
        defaultEmail={contacto.mail ?? ''}
        onCancel={() => setDialog(null)}
        onSubmit={async (recipientEmail) => {
          try {
            const result = await enviarCertificadoEmail(data.id, {
              recipientEmail,
              recipientName: contacto.nombre.trim(),
            });
            if (result.sent) {
              toast.success('Certificado enviado', { description: `A ${result.to}.` });
              setDialog(null);
            } else {
              const friendly =
                result.reason === 'no_recipient'
                  ? 'Falta el correo del destinatario.'
                  : result.reason === 'no_certificado'
                    ? 'La venta no tiene certificado emitido.'
                    : result.reason === 'pdf_fetch_failed'
                      ? 'No se pudo recuperar el PDF del certificado.'
                      : (result.error ?? 'Error desconocido');
              toast.error('No se pudo enviar el certificado', { description: friendly });
            }
          } catch (err) {
            toast.error('No se pudo enviar el certificado', {
              description: err instanceof Error ? err.message : undefined,
            });
          }
        }}
      />
    </div>
  );
}

function detailToRow(d: MantenimientoDetailResponse): MantenimientoRow {
  return {
    id: d.id,
    fields: d.fields,
    progress: d.progress,
    telefono: d.contacto.telefono,
    correo: d.contacto.mail,
  };
}

function ContactoStrip({
  contacto,
  ventaId,
}: {
  contacto: MantenimientoContacto;
  ventaId: string;
}) {
  return (
    <div className="shrink-0 rounded-2xl border bg-card p-4">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Venta Nº {ventaId}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <InfoField label="Nombre" value={contacto.nombre} strong />
        <InfoField label="Mail" value={contacto.mail} />
        <InfoField label="CUIT" value={contacto.cuit} />
        <InfoField label="Teléfono" value={contacto.telefono} />
        <InfoField label="Contacto" value={contacto.medioContacto} />
      </div>
    </div>
  );
}

function InfoField({
  label,
  value,
  strong,
}: {
  label: string;
  value?: string | null;
  strong?: boolean;
}) {
  return (
    <div className="min-w-0 space-y-0.5">
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p
        className={cn(
          'truncate text-sm',
          strong ? 'font-semibold text-foreground' : 'text-foreground',
        )}
        title={value || undefined}
      >
        {value && value.trim() ? value : '-'}
      </p>
    </div>
  );
}

function DespachoCard({
  anulado,
  progress,
  canConfirmarNroDespacho,
  canRealizarDespacho,
  canCambiarHorno,
  cambiarHornoReason,
  onCambiarHorno,
  onViewBitacoras,
  onViewDetalles,
  onOpenNumeros,
  onConfirmarNroDespacho,
  onRealizarDespacho,
}: {
  anulado: boolean;
  progress: { despachado: boolean; nroDespachoConfirmado: boolean };
  canConfirmarNroDespacho: boolean;
  canRealizarDespacho: boolean;
  canCambiarHorno: boolean;
  /** Tooltip shown when the pill is disabled (e.g. Nova-flow venta). */
  cambiarHornoReason?: string;
  onCambiarHorno: () => void;
  onViewBitacoras: () => void;
  onViewDetalles: () => void;
  onOpenNumeros: () => void;
  onConfirmarNroDespacho: () => void;
  onRealizarDespacho: () => void;
}) {
  return (
    <div className="shrink-0 rounded-2xl border bg-card p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-base font-semibold text-foreground">
          <Package className="h-5 w-5" aria-hidden />
          Despacho
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <IconBtn
            onClick={onViewBitacoras}
            label="Bitácoras"
            icon={<ClipboardList className="h-4 w-4" aria-hidden />}
          />
          <IconBtn
            onClick={onViewDetalles}
            label="Ver detalle de venta"
            icon={<ShoppingCart className="h-4 w-4" aria-hidden />}
          />
          <ActionPill
            label="Cambiar horno"
            state="neutral"
            leading={<Repeat className="h-4 w-4" aria-hidden />}
            onClick={onCambiarHorno}
            disabled={!canCambiarHorno}
            title={
              canCambiarHorno
                ? 'Pedir el cambio de un horno (requiere aprobación)'
                : cambiarHornoReason
            }
          />
          <ActionPill
            label="Num. de Serie | Despacho"
            state="neutral"
            leading={<Eye className="h-4 w-4" aria-hidden />}
            onClick={onOpenNumeros}
            disabled={anulado}
          />
          <ActionPill
            label={
              progress.nroDespachoConfirmado
                ? 'N° Despacho Confirmado'
                : 'Confirmar N° Despacho'
            }
            state={progress.nroDespachoConfirmado ? 'done' : 'action'}
            leading={<Check className="h-4 w-4" aria-hidden />}
            onClick={onConfirmarNroDespacho}
            disabled={!canConfirmarNroDespacho}
          />
          <ActionPill
            label={progress.despachado ? 'Despacho Realizado' : 'Realizar Despacho'}
            state={progress.despachado ? 'done' : 'action'}
            trailing={
              progress.despachado ? <Check className="h-4 w-4" aria-hidden /> : null
            }
            leading={
              progress.despachado ? (
                <Eye className="h-4 w-4" aria-hidden />
              ) : (
                <Truck className="h-4 w-4" aria-hidden />
              )
            }
            onClick={onRealizarDespacho}
            // Stays clickable even when done — clicking flips the
            // dialog target to the read-only viewer (handled by the
            // parent's onRealizarDespacho).
            disabled={anulado || (!progress.despachado && !canRealizarDespacho)}
            title={
              !progress.despachado && !progress.nroDespachoConfirmado && !anulado
                ? 'Confirmá el N° de Despacho antes de realizar el despacho'
                : undefined
            }
          />
        </div>
      </div>
    </div>
  );
}

function PuestaMarchaCard({
  canPuestaMarcha,
  canCertificado,
  progress,
  certificadoDriveItemId,
  onPuestaMarcha,
  onCertificado,
  onEnviarCertificadoEmail,
}: {
  canPuestaMarcha: boolean;
  canCertificado: boolean;
  progress: {
    puestaMarchaCargada: boolean;
    certificadoCargado: boolean;
  };
  /** When set + certificadoCargado, the pill turns into a "Ver" link. */
  certificadoDriveItemId: string | null;
  onPuestaMarcha: () => void;
  onCertificado: () => void;
  /** Fired by the "Enviar por correo" pill once the certificado is issued. */
  onEnviarCertificadoEmail: () => void;
}) {
  // Inline preview dialog state — replaces the previous
  // "open in new tab" behavior so the user can review the
  // certificate without losing this page's context. The dialog
  // also exposes a download button that saves the PDF.
  const showCertificadoLink =
    progress.certificadoCargado && Boolean(certificadoDriveItemId);
  const [certificadoPreviewOpen, setCertificadoPreviewOpen] = useState(false);

  return (
    <div className="shrink-0 rounded-2xl border bg-card p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-base font-semibold text-foreground">
          <CheckCheck className="h-5 w-5" aria-hidden />
          Puesta en marcha
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <ActionPill
            label={
              progress.puestaMarchaCargada
                ? 'Puesta en marcha cargada'
                : 'Puesta en marcha'
            }
            state={progress.puestaMarchaCargada ? 'done' : 'neutral'}
            leading={
              progress.puestaMarchaCargada ? (
                <Eye className="h-4 w-4" aria-hidden />
              ) : (
                <Upload className="h-4 w-4" aria-hidden />
              )
            }
            // Once cargada, the pill flips into a "view archivos"
            // affordance — the parent routes the click to the
            // PuestaMarchaDetallesDialog.
            onClick={onPuestaMarcha}
            disabled={!progress.puestaMarchaCargada && !canPuestaMarcha}
          />
          {showCertificadoLink ? (
            // Already issued — fetch the stored PDF (auth-gated) and
            // open it in a new tab. The "Enviar por correo" pill sits
            // alongside so the admin can send to the cliente without
            // leaving this page.
            <>
              <ActionPill
                label="Ver certificado"
                leading={<Eye className="h-4 w-4" aria-hidden />}
                state="done"
                onClick={() => setCertificadoPreviewOpen(true)}
              />
              <ActionPill
                label="Enviar por correo"
                leading={<Mail className="h-4 w-4" aria-hidden />}
                state="neutral"
                onClick={onEnviarCertificadoEmail}
              />
            </>
          ) : (
            <ActionPill
              label="Certificado de garantía"
              state={progress.certificadoCargado ? 'done' : 'neutral'}
              leading={
                progress.certificadoCargado ? (
                  <Check className="h-4 w-4" aria-hidden />
                ) : (
                  <Award className="h-4 w-4" aria-hidden />
                )
              }
              onClick={onCertificado}
              disabled={!canCertificado}
            />
          )}
        </div>
      </div>

      {/* Inline certificado preview — opens in-page instead of a new
          tab, with a built-in download button. Only mounts when
          there's actually a stored PDF to fetch. */}
      {certificadoDriveItemId && (
        <CertificadoPreviewDialog
          open={certificadoPreviewOpen}
          driveItemId={certificadoDriveItemId}
          onClose={() => setCertificadoPreviewOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Read-only PDF preview for the issued Certificado de Garantía.
 * Fetches the bytes through the auth-gated drive endpoint, builds a
 * blob URL, and renders it in an iframe with a download action. The
 * blob URL is revoked on unmount so we don't leak object refs.
 */
function CertificadoPreviewDialog({
  open,
  driveItemId,
  onClose,
}: {
  open: boolean;
  driveItemId: string;
  onClose: () => void;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let createdUrl: string | null = null;
    setError(null);
    setSrc(null);
    (async () => {
      try {
        const res = await fetch(bitacoraPhotoUrl(driveItemId), {
          headers: authHeaders(),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setSrc(createdUrl);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Error desconocido');
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [open, driveItemId]);

  const handleDownload = () => {
    if (!src) return;
    const a = document.createElement('a');
    a.href = src;
    a.download = 'Certificado-Garantia.pdf';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4" aria-hidden />
            Vista previa · Certificado de Garantía
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {error ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              No se pudo cargar el certificado. {error}
            </div>
          ) : !src ? (
            <div className="flex h-[70vh] items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            </div>
          ) : (
            <div className="overflow-hidden rounded-md border">
              <iframe
                title="Vista previa certificado"
                src={src}
                className="h-[70vh] w-full bg-muted"
              />
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
          <Button
            onClick={handleDownload}
            disabled={!src}
            className="gap-2"
          >
            <Download className="h-4 w-4" aria-hidden />
            Descargar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Error dialog shown when the admin tries to confirm the despacho
 * numbers but at least one line is missing its NroSerie or
 * NroDespacho. Lists the offending articles + offers a shortcut into
 * the editor so the admin can resolve and retry.
 */
function MissingNumerosDialog({
  open,
  items,
  onClose,
  onOpenNumeros,
}: {
  open: boolean;
  items: Array<{ id: string; articulo: string; nroSerie: string; nroDespacho: string }>;
  onClose: () => void;
  onOpenNumeros: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden />
            Datos faltantes
          </DialogTitle>
          <DialogDescription>
            No se puede confirmar el N° de Despacho hasta completar
            los siguientes artículos.
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-4">
          <ul className="space-y-1.5 rounded-md border bg-muted/40 px-3 py-2 text-sm">
            {items.map((d) => {
              const missing = [
                !d.nroSerie.trim() ? 'N° Serie' : null,
                !d.nroDespacho.trim() ? 'N° Despacho' : null,
              ].filter(Boolean);
              return (
                <li key={d.id} className="flex items-start justify-between gap-3">
                  <span className="font-medium text-foreground">{d.articulo}</span>
                  <span className="shrink-0 text-xs font-medium text-amber-700">
                    Falta: {missing.join(' · ')}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
          <Button onClick={onOpenNumeros}>Completar números</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IconBtn({
  onClick,
  label,
  icon,
}: {
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="inline-flex h-9 w-9 items-center justify-center rounded-md border text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
    >
      {icon}
    </button>
  );
}

/**
 * Pill-shaped action button that communicates one of three states:
 *   - `action`: hot call-to-action, foreground fill.
 *   - `done`: completed state, muted green.
 *   - `neutral`: available action, light background.
 * Disabled variants fade the fill.
 */
function ActionPill({
  label,
  state,
  leading,
  trailing,
  onClick,
  disabled,
  title,
}: {
  label: string;
  state: 'action' | 'done' | 'neutral';
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** Hover tooltip — useful when the pill is disabled and we want
   *  to explain *why* (e.g. "Confirmá el N° de Despacho primero"). */
  title?: string;
}) {
  const cls =
    state === 'done'
      ? 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100'
      : state === 'action'
        ? 'bg-foreground text-background border-foreground hover:bg-foreground/90'
        : 'bg-card text-foreground border hover:bg-secondary';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        cls,
      )}
    >
      {leading}
      <span>{label}</span>
      {trailing}
    </button>
  );
}

